import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { DialogoRangoFechas, FP } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import {
  COLUMNAS_606,
  construirMatriz606,
  descargarFormato606,
  nombreArchivo606,
  periodoDesdeFecha,
} from './formato606Shared';

const fmtNum = (v) => {
  if (v === '' || v == null) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const esColumnaMonto = (id) =>
  id.startsWith('monto_') ||
  id.startsWith('itbis_') ||
  id.startsWith('isr_') ||
  id === 'total_facturado' ||
  id === 'isc' ||
  id === 'otros_impuestos' ||
  id === 'propina_legal';

/**
 * Pantalla reutilizable del Formato 606 (DGII).
 * Abre un rango de fechas, consulta las compras con NCF y muestra
 * el archivo en pantalla (con descarga .xlsx al estilo 606.xlsm).
 */
const Formato606 = ({ abierto = false, onCerrar }) => {
  const { controller, selectedCompany, showError, showSuccess } = useProductosMaterialContext();
  const { usuarioLog, claveLog, rnc_empresa: rncEmpresaCtx, nombreNegocio } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [cargando, setCargando] = useState(false);
  const [vistaAbierta, setVistaAbierta] = useState(false);
  const [filas, setFilas] = useState([]);
  const [periodo, setPeriodo] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFinal, setFechaFinal] = useState('');
  const [rncEmpresa, setRncEmpresa] = useState('');

  const rncEmpresaResuelto = useMemo(() => {
    const deEmpresa =
      selectedCompany?.rnc_empresa || selectedCompany?.rncEmpresa || selectedCompany?.rnc || '';
    return String(rncEmpresa || deEmpresa || rncEmpresaCtx || '').replace(/\D/g, '');
  }, [rncEmpresa, selectedCompany, rncEmpresaCtx]);

  const nombreEmpresa =
    selectedCompany?.name || selectedCompany?.empresa || nombreNegocio || 'Empresa';

  const reset = useCallback(() => {
    setCargando(false);
    setVistaAbierta(false);
    setFilas([]);
    setPeriodo('');
    setFechaInicio('');
    setFechaFinal('');
    setRncEmpresa('');
  }, []);

  useEffect(() => {
    if (!abierto) reset();
  }, [abierto, reset]);

  const cerrarTodo = useCallback(() => {
    reset();
    onCerrar?.();
  }, [onCerrar, reset]);

  const generar = useCallback(
    async ({ fechaInicio: desde, fechaFinal: hasta }) => {
      if (!selectedCompany?.db_empresa) {
        showError('Seleccione una empresa con base de datos asignada');
        return;
      }
      setCargando(true);
      try {
        const response = await fetch(`${servidor}/contabilidadbackend_formato606`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            fecha_desde: desde,
            fecha_hasta: hasta,
            rnc_empresa: String(rncEmpresaCtx || selectedCompany?.rnc || '').replace(/\D/g, ''),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          showError(data.Error || 'No se pudo generar el Formato 606');
          return;
        }
        const registros = data.filas || [];
        const per = data.periodo || periodoDesdeFecha(hasta);
        const rnc = String(data.rnc_empresa || rncEmpresaCtx || '').replace(/\D/g, '');
        setFilas(registros);
        setPeriodo(per);
        setFechaInicio(desde);
        setFechaFinal(hasta);
        setRncEmpresa(rnc);
        const matriz = construirMatriz606({
          rncEmpresa: rnc,
          periodo: per,
          filas: registros,
        });
        descargarFormato606(matriz, nombreArchivo606(per));
        setVistaAbierta(true);
        showSuccess(
          registros.length
            ? `Formato 606 generado · ${registros.length} registro(s)`
            : 'Formato 606 generado · sin comprobantes en el período'
        );
      } catch {
        showError('Error de conexión al generar el Formato 606');
      } finally {
        setCargando(false);
      }
    },
    [servidor, selectedCompany, usuarioLog, claveLog, rncEmpresaCtx, showError, showSuccess]
  );

  const descargarDeNuevo = useCallback(() => {
    const matriz = construirMatriz606({
      rncEmpresa: rncEmpresaResuelto,
      periodo,
      filas,
    });
    descargarFormato606(matriz, nombreArchivo606(periodo));
  }, [filas, periodo, rncEmpresaResuelto]);

  return (
    <>
      <DialogoRangoFechas
        abierto={Boolean(abierto) && !vistaAbierta}
        titulo="Formato 606"
        mensaje="Indique la fecha de inicio y la fecha final de las compras a reportar. Se creará un archivo como 606.xlsm y se abrirá en pantalla."
        textoConfirmar="Generar 606"
        cargando={cargando}
        onCerrar={cerrarTodo}
        onConfirmar={generar}
      />

      <Dialog
        open={vistaAbierta}
        onClose={cerrarTodo}
        maxWidth={false}
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 1.5 },
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            m: { xs: 0, md: 1 },
            width: { xs: '100vw', md: 'calc(100vw - 16px)' },
            maxWidth: '100vw',
            height: { xs: '100vh', md: 'calc(100vh - 16px)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pr: 1,
            borderBottom: `1px solid ${FP.color.border}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.1rem' }}>
              Herramienta Formato 606
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 0.25 }}>
              {nombreEmpresa}
              {rncEmpresaResuelto ? ` · RNC ${rncEmpresaResuelto}` : ''}
              {periodo ? ` · Período ${periodo}` : ''}
              {fechaInicio && fechaFinal ? ` · ${fechaInicio} al ${fechaFinal}` : ''}
              {` · ${filas.length} registro(s)`}
            </Typography>
          </Box>
          <IconButton onClick={cerrarTodo} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: FP.color.surface, display: 'flex', flexDirection: 'column' }}>
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filas.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No hay comprobantes fiscales (NCF) en el período seleccionado.
            </Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLUMNAS_606.map((c) => (
                      <TableCell
                        key={c.id}
                        align={esColumnaMonto(c.id) ? 'right' : 'left'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          bgcolor: FP.color.navy,
                          color: '#FFFFFF',
                        }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filas.map((row, idx) => (
                    <TableRow key={`${row.ncf || 'row'}-${idx}`} hover>
                      {COLUMNAS_606.map((c) => (
                        <TableCell
                          key={c.id}
                          align={esColumnaMonto(c.id) ? 'right' : 'left'}
                          sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                        >
                          {esColumnaMonto(c.id) ? fmtNum(row[c.id]) : row[c.id] || ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${FP.color.border}` }}>
          <Button
            variant="outlined"
            onClick={cerrarTodo}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={descargarDeNuevo}
            disabled={filas.length === 0}
            sx={{
              bgcolor: FP.color.navy,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Descargar Excel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Formato606;
